import { useEffect, useRef, useState } from 'react';
import MobileFrame from '@/components/MobileFrame';
import { Icon } from '@/components/Icons';
import { useT } from '@/lib/i18n';
import { getProfile, updateSpecimen } from '@/lib/auth-api';
import { dataUrlToBlob } from '@/lib/onboarding-api';

export default function SpecimenScreen() {
    const t = useT();
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const drawingRef = useRef(false);
    const lastRef = useRef<{ x: number; y: number } | null>(null);

    const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
    const [hasDrawn, setHasDrawn] = useState(false);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState<string | null>(null);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        getProfile()
            .then((p) => setCurrentImageUrl(p.specimen?.imageUrl ?? null))
            .catch(() => {
                /* silent */
            });
    }, []);

    useEffect(() => {
        const c = canvasRef.current;
        if (!c) return;
        const dpr = window.devicePixelRatio || 1;
        c.width = c.offsetWidth * dpr;
        c.height = c.offsetHeight * dpr;
        const ctx = c.getContext('2d');
        if (!ctx) return;
        ctx.scale(dpr, dpr);
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#0F1419';
    }, []);

    const getPoint = (
        e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
    ) => {
        const c = canvasRef.current;
        if (!c) return { x: 0, y: 0 };
        const rect = c.getBoundingClientRect();
        if ('touches' in e) {
            return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
        }
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const start = (
        e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
    ) => {
        e.preventDefault();
        drawingRef.current = true;
        lastRef.current = getPoint(e);
        setHasDrawn(true);
    };
    const move = (
        e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
    ) => {
        if (!drawingRef.current) return;
        e.preventDefault();
        const p = getPoint(e);
        const ctx = canvasRef.current?.getContext('2d');
        if (!ctx || !lastRef.current) return;
        ctx.beginPath();
        ctx.moveTo(lastRef.current.x, lastRef.current.y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        lastRef.current = p;
    };
    const end = () => {
        drawingRef.current = false;
    };

    const clear = () => {
        const c = canvasRef.current;
        if (!c) return;
        c.getContext('2d')?.clearRect(0, 0, c.width, c.height);
        setHasDrawn(false);
    };

    const save = async () => {
        const c = canvasRef.current;
        if (!c) return;
        try {
            setSaving(true);
            setErr(null);
            setMsg(null);
            const blob = dataUrlToBlob(c.toDataURL('image/png'));
            const result = await updateSpecimen(blob);
            setCurrentImageUrl(result.imageUrl);
            clear();
            setMsg('Tanda tangan berhasil diperbarui.');
        } catch (e) {
            setErr(e instanceof Error ? e.message : t.somethingWrong);
        } finally {
            setSaving(false);
        }
    };

    return (
        <MobileFrame showBack>
            <div
                className="fade-in"
                style={{ flex: 1, padding: '8px 24px 28px', display: 'flex', flexDirection: 'column' }}
            >
                <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Tanda Tangan Digital</h1>
                <p
                    style={{
                        fontSize: 14,
                        lineHeight: 1.55,
                        color: 'var(--ink-500)',
                        marginBottom: 16,
                    }}
                >
                    Perbarui tanda tangan yang digunakan pada dokumen digital Anda.
                </p>

                {currentImageUrl && (
                    <div className="card" style={{ padding: 14, marginBottom: 14 }}>
                        <div
                            style={{
                                fontSize: 11,
                                color: 'var(--ink-500)',
                                fontWeight: 600,
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                                marginBottom: 8,
                            }}
                        >
                            Tanda Tangan Saat Ini
                        </div>
                        <img
                            src={currentImageUrl}
                            alt="Tanda tangan saat ini"
                            style={{
                                width: '100%',
                                maxHeight: 120,
                                objectFit: 'contain',
                                background: 'var(--ink-50)',
                                borderRadius: 8,
                            }}
                        />
                    </div>
                )}

                <label className="field-label">Gores Tanda Tangan Baru</label>
                <div
                    style={{
                        position: 'relative',
                        width: '100%',
                        height: 220,
                        border: '1.5px dashed var(--ink-300)',
                        borderRadius: 12,
                        background: 'var(--surface)',
                        marginBottom: 12,
                        overflow: 'hidden',
                    }}
                >
                    <canvas
                        ref={canvasRef}
                        style={{ width: '100%', height: '100%', display: 'block', touchAction: 'none' }}
                        onMouseDown={start}
                        onMouseMove={move}
                        onMouseUp={end}
                        onMouseLeave={end}
                        onTouchStart={start}
                        onTouchMove={move}
                        onTouchEnd={end}
                    />
                    {!hasDrawn && (
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                pointerEvents: 'none',
                                color: 'var(--ink-400)',
                            }}
                        >
                            <Icon name="signature" size={32} color="var(--ink-300)" />
                            <span style={{ fontSize: 13, marginTop: 8 }}>{t.signDrawHere}</span>
                        </div>
                    )}
                </div>

                <button
                    type="button"
                    onClick={clear}
                    disabled={!hasDrawn}
                    style={{
                        alignSelf: 'flex-start',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '8px 14px',
                        borderRadius: 8,
                        background: 'var(--ink-100)',
                        color: 'var(--ink-700)',
                        fontSize: 13,
                        fontWeight: 600,
                        opacity: hasDrawn ? 1 : 0.4,
                    }}
                >
                    <Icon name="trash" size={14} />
                    {t.signClear}
                </button>

                {msg && (
                    <div className="chip chip-success" style={{ marginTop: 14, alignSelf: 'flex-start' }}>
                        <Icon name="check" size={14} /> {msg}
                    </div>
                )}
                {err && (
                    <div className="chip chip-error" style={{ marginTop: 14, alignSelf: 'flex-start' }}>
                        <Icon name="alert" size={14} /> {err}
                    </div>
                )}

                <div style={{ flex: 1, minHeight: 20 }} />

                <button
                    type="button"
                    className="btn btn-primary"
                    style={{ width: '100%', marginTop: 12 }}
                    disabled={!hasDrawn || saving}
                    onClick={save}
                >
                    {saving ? <div className="spinner" /> : t.signConfirm}
                </button>
            </div>
        </MobileFrame>
    );
}
